let DBMaster = null;

// Ensure MongoDB is only used on the server
if (typeof window === "undefined") {
  const { MongoClient } = require('mongodb');
  
  // Use global variables to persist across module reloads in development
  global._mongoClient = global._mongoClient || null;
  global._mongoDB = global._mongoDB || null;
  global._mongoIsConnecting = global._mongoIsConnecting || false;
  global._mongoConnectionCount = global._mongoConnectionCount || 0;
  
  let client = global._mongoClient;
  let db = global._mongoDB;
  let isConnecting = global._mongoIsConnecting;
  let connectionCount = global._mongoConnectionCount;
  
  // MongoDB configuration constants
  const { MONGO_DB_NAME, MONGO_DB_URI } = require('../utils/Constants');

  let intlRouteIndexEnsured = false;

  async function ensureIntlRouteIndex(database) {
    try {
      const collection = database.collection('intl_route');
      await collection.createIndex(
        { src_city_code: 1, dest_city_code: 1 },
        { name: 'src_city_code_dest_city_code_1' },
      );
    } catch (err) {
      console.error('intl_route index ensure failed:', err);
    }
  }

  async function createMongoClient() {
    const config = {
      useNewUrlParser: true,
      useUnifiedTopology: true,
      maxPoolSize: 100, // Adjust based on your needs
      minPoolSize: 10,
      connectTimeoutMS: 20000, // 20 seconds
      socketTimeoutMS: 60000, // 60 seconds - safety net for IF route queries
    };

    try {
      client = new MongoClient(MONGO_DB_URI, config);
      await client.connect();
      db = client.db(MONGO_DB_NAME);
      connectionCount++;
      
      // Update global variables
      global._mongoClient = client;
      global._mongoDB = db;
      global._mongoConnectionCount = connectionCount;
      
      console.log(`Connected to MongoDB via DBMaster singleton (connection #${connectionCount})`);

      if (!intlRouteIndexEnsured) {
        intlRouteIndexEnsured = true;
        ensureIntlRouteIndex(db).catch((err) =>
          console.error('intl_route index ensure failed:', err),
        );
      }

      // Add connection event listeners for monitoring
      client.on('close', () => {
        console.log('MongoDB connection closed');
        client = null;
        db = null;
        global._mongoClient = null;
        global._mongoDB = null;
      });
      
      client.on('error', (err) => {
        console.error('MongoDB connection error:', err);
      });
      
      return { client, db };
    } catch (err) {
      console.error("MongoDB Connection Error:", err);
      client = null;
      db = null;
      global._mongoClient = null;
      global._mongoDB = null;
      throw err;
    }
  }

  async function getMongoClient() {
    // If already connected and client is healthy, return existing connection
    if (client && db) {
      try {
        // Use a lighter check - just verify the client is connected
        if (client.topology && client.topology.isConnected()) {
          return { client, db };
        }
        // If topology check fails, try ping as fallback
        await client.db('admin').ping();
        return { client, db };
      } catch (err) {
        console.log("Existing MongoDB connection lost, reconnecting...", err.message);
        client = null;
        db = null;
        global._mongoClient = null;
        global._mongoDB = null;
      }
    }

    // Prevent multiple concurrent connection attempts
    if (isConnecting) {
      // Wait for the ongoing connection attempt
      while (isConnecting) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      // After waiting, check if connection was established
      if (client && db) {
        return { client, db };
      }
    }

    // Create new connection
    if (!client || !db) {
      isConnecting = true;
      global._mongoIsConnecting = true;
      try {
        const connection = await createMongoClient();
        client = connection.client;
        db = connection.db;
      } finally {
        isConnecting = false;
        global._mongoIsConnecting = false;
      }
    }
    return { client, db };
  }

  async function getDatabase() {
    const { db } = await getMongoClient();
    return db;
  }

  async function getCollection(collectionName) {
    const db = await getDatabase();
    return db.collection(collectionName);
  }

  async function closeConnection() {
    if (client) {
      try {
        await client.close();
        client = null;
        db = null;
        console.log("MongoDB connection closed");
      } catch (err) {
        console.error("Error closing MongoDB connection:", err);
        throw err;
      }
    }
  }

  // Health check function
  async function healthCheck() {
    try {
      const { client, db } = await getMongoClient();
      if (!client || !db) {
        return { status: 'unhealthy', message: 'MongoDB client or database not available' };
      }
      
      // Perform ping to verify connection
      await db.admin().ping();
      
      // Additional check: verify we can access the database
      const stats = await db.stats();
      
      return { 
        status: 'healthy', 
        message: 'MongoDB connection is active',
        details: {
          database: db.databaseName,
          collections: stats.collections || 0,
          connectionPoolSize: client.topology?.s?.poolSize || 'unknown',
          totalConnectionsCreated: connectionCount,
          singletonWorking: connectionCount === 1 ? 'YES' : `NO - ${connectionCount} connections created`
        }
      };
    } catch (err) {
      console.error("MongoDB health check failed:", err);
      return { 
        status: 'unhealthy', 
        message: err.message,
        error: err.name
      };
    }
  }

  // Get connection statistics
  function getConnectionStats() {
    return {
      isConnected: !!(client && db),
      totalConnectionsCreated: connectionCount,
      isConnecting: isConnecting,
      databaseName: db?.databaseName || null
    };
  }

  DBMaster = {
    getMongoClient,
    getDatabase,
    getCollection,
    closeConnection,
    healthCheck,
    getConnectionStats,
  };
} else {
  // No-op functions for client-side to avoid runtime errors
  DBMaster = {
    getMongoClient: async () => ({ client: null, db: null }),
    getDatabase: async () => null,
    getCollection: async () => null,
    closeConnection: async () => null,
    healthCheck: async () => ({ status: 'client-side', message: 'MongoDB not available on client' }),
    getConnectionStats: () => ({ isConnected: false, totalConnectionsCreated: 0, isConnecting: false, databaseName: null }),
  };
}

export default DBMaster;
